import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import get from "lodash/get";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import PageNotFound from "../../pages/PageNotFound";
import orderService from "../../services/order";
import { rupiah } from "../../utils/currency";
import cookie from "../../utils/useCookie";
import { useNavigate } from "react-router-dom";

// Custom debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Empty state component
const NoOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No orders found
    </Typography>
  </Box>
);

// Define order interface
interface Order {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  amount: number;
  voucher_code: string | null;
  created_time: string;
  created_by: string | null;
  event_id: number;
  discount: number;
  tax: number;
  fee: number;
  total: number;
  gender: string | null;
  location: string | null;
  tickets?: Array<{
    category: string;
    quantity: number;
  }>;
}

// Mobile card view component
const OrderCard = ({
  order,
  onViewDetails,
  onResendEmail,
}: {
  order: Order;
  onViewDetails: (order: Order) => void;
  onResendEmail: (orderId: string) => void;
}) => (
  <Card sx={{ p: 2, mb: 2 }}>
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="h6"
          color="primary"
          sx={{
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => onViewDetails(order)}
        >
          Order #{order.id.slice(0, 8)}...
        </Typography>
        <Chip
          label={order.status}
          color={
            order.status === "success"
              ? "success"
              : order.status === "pending"
              ? "warning"
              : "error"
          }
          size="small"
        />
      </Stack>

      <Stack spacing={1}>
        {/* Customer Information */}
        <Box sx={{ bgcolor: "grey.50", p: 1.5, borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Customer Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2">
                {order.name} ({order.gender || "Not specified"})
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                📧 {order.email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                📱 {order.phone}
              </Typography>
            </Grid>
            {order.location && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  📍 {order.location}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Add tickets section */}
        {order.tickets && order.tickets.length > 0 && (
          <Box sx={{ bgcolor: "grey.50", p: 1.5, borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tickets
            </Typography>
            <Stack spacing={1}>
              {order.tickets.map((ticket, index) => (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2">{ticket.category}</Typography>
                  <Chip
                    label={`x${ticket.quantity}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              ))}
              <Typography variant="body2" color="text.secondary" align="right">
                Total: {order.tickets.reduce((sum, t) => sum + t.quantity, 0)}{" "}
                tickets
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Price Breakdown */}
        <Box sx={{ bgcolor: "grey.50", p: 1.5, borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Price Details
          </Typography>
          <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Subtotal:
              </Typography>
              <Typography variant="body2">{rupiah(order.amount)}</Typography>
            </Stack>
            {order.discount > 0 && (
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="success.main">
                  Discount {order.voucher_code && `(${order.voucher_code})`}:
                </Typography>
                <Typography variant="body2" color="success.main">
                  -{rupiah(order.discount)}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Tax:
              </Typography>
              <Typography variant="body2">{rupiah(order.tax)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Fee:
              </Typography>
              <Typography variant="body2">{rupiah(order.fee)}</Typography>
            </Stack>
            <Divider sx={{ my: 0.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2">Total:</Typography>
              <Typography variant="subtitle2" color="primary">
                {rupiah(order.total)}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Created: {new Date(order.created_time).toLocaleString()}
          {order.created_by && ` by ${order.created_by}`}
        </Typography>

        {/* Add resend email button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onResendEmail(order.id)}
          >
            Resend Email
          </Button>
        </Box>
      </Stack>
    </Stack>
  </Card>
);

export default function Order({ event_id }: { event_id: number }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState<string>("");
  const debouncedKeyword = useDebounce(keyword, 500); // 500ms debounce
  const user = JSON.parse(cookie.getCookie("USER") || "");
  const notification = useNotification();
  const alert = useAlert();
  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Mobile view specific state
  const [mobileItems, setMobileItems] = useState<Order[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(false);

  // Email change dialog states
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>("");
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);

  // Add validation for user permissions
  const hasAccess =
    user.role_id === 3 || user.events?.includes(Number(event_id));

  const {
    data: orders,
    isLoading,
    isError,
  } = useQuery(
    ["orders", event_id, debouncedKeyword, status, page, pageSize],
    () =>
      orderService.getAllOrder({
        event_id,
        keyword: debouncedKeyword,
        status: status || undefined,
        page: page + 1, // Convert from 0-based to 1-based indexing for backend
        limit: pageSize,
      }),
    {
      enabled: !!event_id && hasAccess,
      onSuccess: (data) => {
        if (isMobile) {
          if (page === 0) {
            setMobileItems(get(data, "data", []));
          } else {
            setMobileItems((prev) => [...prev, ...get(data, "data", [])]);
          }

          // Check if we have more data to load
          setHasMore(get(data, "data", []).length === pageSize);
          setMobileLoading(false);
        }
      },
    }
  );

  // Reset pagination when filters change
  useEffect(() => {
    setPage(0);
    if (isMobile) {
      setMobileItems([]);
    }
  }, [debouncedKeyword, status, isMobile]);

  if (!hasAccess) {
    return <PageNotFound />;
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const loadMore = () => {
    setMobileLoading(true);
    setPage((prevPage) => prevPage + 1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  };

  const handleResetFilters = () => {
    setKeyword("");
    setStatus("");
    setPage(0);
  };

  const handleResendEmail = async (orderId: string) => {
    alert.onClose();
    try {
      await orderService.resendEmail(orderId);
      notification.onOpen({
        message: "Email has been resent",
        type: "success",
        position: "top",
      });
    } catch (error) {
      console.error("Failed to resend email", error);
      notification.onOpen({
        message: "Failed to resend email",
        type: "error",
        position: "top",
      });
    }
  };

  const handleClickResentEmail = (orderId: string) => {
    alert.onOpen({
      title: "Resend Email",
      message: "Are you sure want to resend email?",
      actions: [
        {
          color: "primary",
          variant: "contained",
          children: "Resend",
          onClick: () => handleResendEmail(orderId),
        },
        {
          color: "error",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const handleChangeEmail = async () => {
    if (!newEmail || !selectedOrderId) return;

    setEmailChangeLoading(true);
    try {
      await orderService.changeEmail(selectedOrderId, newEmail);
      notification.onOpen({
        message: "Email has been changed successfully",
        type: "success",
        position: "top",
      });
      setEmailDialogOpen(false);
      setNewEmail("");
      setSelectedOrderId("");
      // Refetch orders to update the data
      window.location.reload();
    } catch (error) {
      console.error("Failed to change email", error);
      notification.onOpen({
        message: "Failed to change email",
        type: "error",
        position: "top",
      });
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleClickChangeEmail = (orderId: string, currentEmail: string) => {
    setSelectedOrderId(orderId);
    setNewEmail(currentEmail);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setNewEmail("");
    setSelectedOrderId("");
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Order ID",
      width: 350,
      renderCell: (params: any) => params.value,
    },
    {
      field: "name",
      headerName: "Customer",
      flex: 1,
      width: 180,
      renderCell: (params: any) => (
        <Stack>
          <Typography variant="body2">{params.value}</Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.email}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "tickets",
      headerName: "Tickets",
      flex: 1,
      minWidth: 400,
      renderCell: (params: any) => {
        const tickets = params.value || [];
        if (tickets.length === 0)
          return <Typography variant="body2">No tickets</Typography>;

        return (
          <Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {tickets.map((ticket: any, index: number) => (
                <Chip
                  key={index}
                  label={`${ticket.quantity}× ${ticket.category}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ my: 0.2 }}
                />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Total:{" "}
              {tickets.reduce((sum: number, t: any) => sum + t.quantity, 0)}{" "}
              tickets
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: 130,
    },
    {
      field: "total",
      headerName: "Total Amount",
      width: 150,
      align: "right",
      headerAlign: "right",
      renderCell: (params: any) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2">{rupiah(params.value)}</Typography>
          {params.row.discount > 0 && (
            <Typography variant="caption" color="success.main">
              -{rupiah(params.row.discount)} off
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          color={
            params.value === "success"
              ? "success"
              : params.value === "pending"
              ? "warning"
              : "error"
          }
          size="small"
        />
      ),
    },
    {
      field: "created_time",
      headerName: "Order Date",
      width: 180,
      renderCell: (params: any) => (
        <Stack>
          <Typography variant="body2">
            {new Date(params.value).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(params.value).toLocaleTimeString()}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: user.role_id === 3 ? 200 : 130,
      sortable: false,
      renderCell: (params: any) => {
        if (params.row.status === "success") {
          return (
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleClickResentEmail(params.row.id)}
              >
                Resend Email
              </Button>
              {user.role_id === 3 && (
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  onClick={() =>
                    handleClickChangeEmail(params.row.id, params.row.email)
                  }
                >
                  Change Email
                </Button>
              )}
            </Stack>
          );
        } else {
          return <></>;
        }
      },
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>Order List</title>
      </Helmet>

      <Stack direction="row" alignItems="center">
        <IconButton size="small" sx={{ mt: 0.5 }} onClick={() => navigate(-1)}>
          <ArrowBackIosRoundedIcon fontSize="inherit" />
        </IconButton>

        <Breadcrumb
          breadcrumbs={[{ label: "Events", href: `/admin/event` }]}
          label={"Order List"}
        />
      </Stack>
      <Box sx={{ mt: 2 }}>
        <Card elevation={2} sx={{ p: 2 }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            Event Orders
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <TextField
              label="Search Orders"
              variant="outlined"
              fullWidth
              value={keyword}
              onChange={handleSearchChange}
            />

            <FormControl sx={{ minWidth: { xs: "100%", md: 200 } }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={status}
                label="Status"
                onChange={handleStatusChange}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={handleResetFilters}
              sx={{ minWidth: { xs: "100%", md: 120 } }}
            >
              Reset Filters
            </Button>
          </Stack>

          {isLoading && page === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography color="error">
                Failed to load orders. Please try again.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {mobileItems.map((order: Order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onViewDetails={() => console.log("")}
                  onResendEmail={handleClickResentEmail}
                />
              ))}
              {mobileItems.length === 0 && <NoOverlay />}
              {hasMore && (
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={loadMore}
                  disabled={mobileLoading}
                  sx={{ mt: 2 }}
                >
                  {mobileLoading ? (
                    <CircularProgress size={24} sx={{ mx: 1 }} />
                  ) : (
                    "Load More"
                  )}
                </Button>
              )}
            </Stack>
          ) : (
            <DataGrid
              autoHeight
              disableSelectionOnClick
              loading={isLoading}
              rows={get(orders, "data", [])}
              getRowId={(row) => row.id}
              components={{
                NoRowsOverlay: NoOverlay,
              }}
              columns={columns}
              pagination
              paginationMode="server"
              page={page}
              pageSize={pageSize}
              rowCount={get(orders, "meta.total", 0)}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "background.neutral",
                  borderRadius: "8px 8px 0 0",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  color: "text.primary",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "action.hover",
                },
                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                  {
                    outline: "none",
                  },
              }}
            />
          )}
        </Card>
      </Box>

      {/* Email Change Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Customer Email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailChangeLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeEmail}
            variant="contained"
            disabled={!newEmail || emailChangeLoading}
          >
            {emailChangeLoading ? <CircularProgress size={20} /> : "Change Email"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
